import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { usePaymentGroups } from '@/modules/common/hooks/data-hooks/usePaymentGroups';
import { useGetPayments } from '@/modules/payments/hooks/usePayments';
import { PaymentInclude, PaymentStatus, PaymentType } from '@/types/payment';
import { PaymentGroupStage, PaymentGroupType } from '@/types/payment-groups';
import { UserRole } from '@/types/user';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER } from '../constants/payment-group-stage';
import { PaymentPageStage } from '../types';

export function usePaymentsData(
  stage: PaymentPageStage,
  columnFilters: ColumnFiltersState,
  paginationState: PaginationState,
) {
  const { currentUser, taxBenefitsProvider } = useCurrentUserContext() ?? {};

  const educationProviderFilter = columnFilters.find(
    filter => filter.id === 'educationProvider',
  )?.value as string[] | undefined;

  const statusFilter = columnFilters.find(filter => filter.id === 'status')
    ?.value as PaymentStatus[] | undefined;

  const effectiveStatusFilter = statusFilter?.length
    ? statusFilter
    : stage && stage !== PaymentPageStage.ALL
      ? (() => {
          const stageMapping: Record<PaymentPageStage, PaymentGroupStage> = {
            [PaymentPageStage.ALL]: PaymentGroupStage.AWAITING_PAYMENT,
            [PaymentPageStage.AWAITING_PAYMENT]:
              PaymentGroupStage.AWAITING_PAYMENT,
            [PaymentPageStage.IN_PROGRESS]: PaymentGroupStage.IN_PROGRESS,
            [PaymentPageStage.FAILED]: PaymentGroupStage.FAILED,
            [PaymentPageStage.COMPLETED]: PaymentGroupStage.COMPLETED,
          };

          const groupStage = stageMapping[stage];
          return PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER[groupStage] || [];
        })()
      : undefined;

  const {
    isLoading: isLoadingPayments,
    data: {
      data: payments = [],
      metadata: { total: paymentsTotal = 0 } = {},
    } = {},
  } = useGetPayments(
    {
      paymentType:
        (columnFilters.find(filter => filter.id === 'type')?.value as
          | PaymentType
          | undefined) ?? undefined,
      ...(currentUser?.roles.includes(UserRole.TaxBenefitsProvider) &&
        taxBenefitsProvider?._id && {
          payer: taxBenefitsProvider._id,
        }),
      ...(effectiveStatusFilter && { status: effectiveStatusFilter }),
      ...(educationProviderFilter?.length && {
        educationProvider: educationProviderFilter,
      }),
    },
    {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
    },
    [PaymentInclude.Course, PaymentInclude.Order, PaymentInclude.User],
    {
      createdAt: 'desc',
    },
    {
      placeholderData: keepPreviousData,
      enabled:
        !!currentUser &&
        (!stage ||
          [PaymentPageStage.ALL, PaymentPageStage.AWAITING_PAYMENT].includes(
            stage,
          )),
    },
  );

  const {
    isLoading: isLoadingPaymentGroups,
    isRefetching: isRefetchingPaymentGroups,
    data: {
      data: paymentGroups = [],
      metadata: { total: paymentGroupsTotal = 0 } = {},
    } = {},
  } = usePaymentGroups({
    filters: {
      ...(Array.isArray(statusFilter) && statusFilter.length
        ? { status: statusFilter }
        : { stage: stage }),
      ...(educationProviderFilter?.length && {
        educationProvider: educationProviderFilter,
      }),
      ...(((columnFilters.find(filter => filter.id === 'paymentGroupType')
        ?.value as PaymentGroupType | undefined) && {
        type: columnFilters.find(filter => filter.id === 'paymentGroupType')
          ?.value as PaymentType | undefined,
      }) ??
        undefined),
    },
    pagination: {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
    },
    sort: {
      createdAt: 'desc',
    },
    options: {
      enabled:
        !!currentUser &&
        stage &&
        ![PaymentPageStage.ALL, PaymentPageStage.AWAITING_PAYMENT].includes(
          stage,
        ),
    },
  });

  const shouldRenderPaymentsTable =
    !stage ||
    [PaymentPageStage.ALL, PaymentPageStage.AWAITING_PAYMENT].includes(stage);

  const isLoading = shouldRenderPaymentsTable
    ? isLoadingPayments
    : isLoadingPaymentGroups || isRefetchingPaymentGroups;
  const total = shouldRenderPaymentsTable ? paymentsTotal : paymentGroupsTotal;
  const data = shouldRenderPaymentsTable ? payments : paymentGroups;

  return {
    data,
    total,
    isLoading,
    shouldRenderPaymentsTable,
    payments,
    paymentGroups,
  };
}
